import { apps } from '../apps'
import { appName as eventsAppName } from '../apps/events/appConfig'

const MS_PER_DAY = 24 * 60 * 60 * 1000

// Cross-app calendar items, tagged with which app they came from.
//
// Any app opts in by exposing `calendarEvents` + `calendarColor` on its appDefinition —
// the same two fields the landing-page ActivityWidget reads (see hooks/useActivity.js).
// This helper is the *range* half of that contract, for surfaces that show an explicit
// window rather than "the next N days": the events calendar navigating month by month,
// including into the past.
//
// A provider receives BOTH `{start, end}` and a `daysAhead` derived from the window. A
// range-aware provider should use start/end; one that only understands `daysAhead` still
// returns something, and any item outside the window simply doesn't match a day cell, so
// it degrades to invisible rather than wrong.
//
// The events app is skipped: every caller fetches its own rows natively (it owns the
// event model, including interest toggles and guest counts, which a provider item has no
// equivalent for).
export const fetchCalendarProviderItems = async (start, end) => {
  const contributingApps = Object.values(apps)
    .filter(app => app.name !== eventsAppName && typeof app.calendarEvents === 'function')

  const daysAhead = Math.max(1, Math.ceil((new Date(end) - new Date(start)) / MS_PER_DAY))

  const results = await Promise.all(
    contributingApps.map(app =>
      app.calendarEvents({ start, end, daysAhead })
        .then(items => (items || []).map(item => ({
          ...item,
          source:      app.name,
          sourceLabel: app.displayName,
          sourceColor: app.calendarColor || 'var(--color-dark-grey)',
        })))
        // One broken provider must not empty the calendar — log and carry on, same as
        // useActivity. Note this is why a 500 in one app's endpoint shows up as silently
        // missing items rather than an error: check the network tab if something's absent.
        .catch(err => { console.log(err); return [] })
    )
  )

  return results.flat()
}

// Normalize a provider item into the shape the events calendar's own renderers expect, so
// one code path draws both. `external: true` is what makes a chip/block link out to the
// owning app instead of the event editor, and carries the source colour.
//
// An item's optional `participation`/`ics` (the signup toggle + calendar file the landing-page
// ActivityWidget offers — see recruitment's helpers/calendarEvents.js) are deliberately NOT
// mapped through. This is a whitelist, so they simply don't reach the calendar: /events/calendar
// has no interest toggle even for its own native events, and adding one only for other apps'
// items would be the odd surface out. If it ever grows one, it should grow it for both, and
// then these two keys are what it reads.
export const asCalendarEntry = (item) => ({
  event_slug:            item.key,
  event_title:           item.title,
  event_start_timestamp: item.start,
  // `end === start` is how a provider says "no known end" — the ActivityWidget's contract
  // wants a real date there, so providers fill it in. A calendar wants the opposite: null,
  // so the same "assume an hour" default an endless event gets applies. Passing the
  // start through as the end instead renders a zero-height block clamped to a 4px sliver.
  event_end_timestamp:   item.end && item.end !== item.start ? item.end : null,
  // Provider items are shown on weekends: unlike an events row, they have no
  // "include weekends" flag to honour, and hiding an interview booked on a Saturday
  // would be a lie rather than a tidy-up.
  event_include_weekends: true,
  external:    true,
  url:         item.url,
  source:      item.source,
  sourceLabel: item.sourceLabel,
  sourceColor: item.sourceColor,
})
